import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import {
  lessonsApi,
  recordingsApi,
  type Lesson,
  type Participant,
} from "../lib/lessons";

function fmt(ts: number): string {
  return new Date(ts).toLocaleString("pl-PL", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function LessonPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [recordings, setRecordings] = useState<Array<{ id: string; status: string; ended_at: number | null }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id || !user) return;
    lessonsApi
      .get(id)
      .then(({ lesson, participants }) => {
        setLesson(lesson);
        setParticipants(participants);
      })
      .catch((e) => setError(String(e)));
    recordingsApi
      .forLesson(id)
      .then((r) => setRecordings(r.recordings))
      .catch(() => {});
  }, [id, user]);

  if (!user) return <Link to="/" className="text-white p-6 block">Zaloguj się</Link>;
  if (error) return <div className="p-6 text-red-300">{error}</div>;
  if (!lesson) return <div className="p-6 text-ink-400">Wczytywanie…</div>;

  const isHost = lesson.host_user_id === user.profile.sub;
  const t = Date.now();
  const joinable =
    lesson.status !== "cancelled" &&
    t >= lesson.starts_at - 10 * 60 * 1000 &&
    t <= lesson.ends_at + 10 * 60 * 1000;

  async function joinRoom(): Promise<void> {
    navigate(`/room/${id}`);
  }

  async function cancel(): Promise<void> {
    if (!confirm("Anulować lekcję?")) return;
    setBusy(true);
    try {
      await lessonsApi.cancel(lesson!.id);
      setLesson({ ...lesson!, status: "cancelled" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full bg-ink-900 text-white">
      <header className="px-6 py-4 border-b border-ink-700">
        <Link to="/" className="text-xs text-ink-400 hover:text-white">← Wróć</Link>
        <div className="flex items-baseline justify-between mt-1">
          <h1 className="text-lg font-semibold">{lesson.title}</h1>
          <span className="text-xs text-ink-400">{lesson.status}</span>
        </div>
        <p className="text-sm text-ink-300 mt-1">
          {fmt(lesson.starts_at)} – {fmt(lesson.ends_at)}
        </p>
      </header>

      <main className="max-w-2xl mx-auto p-6 space-y-6">
        <section>
          <button
            disabled={!joinable}
            onClick={joinRoom}
            className="w-full bg-lime-500 hover:bg-lime-400 disabled:bg-ink-700 disabled:text-ink-500 text-ink-900 font-semibold rounded-lg py-3"
          >
            {joinable ? "Dołącz do pokoju" : "Dostępne 10 min przed startem"}
          </button>
        </section>

        <section>
          <h2 className="text-sm uppercase tracking-wider text-ink-400 mb-2">
            Uczestnicy ({participants.length} / {lesson.max_participants})
          </h2>
          <ul className="bg-ink-800 border border-ink-700 rounded-2xl divide-y divide-ink-700">
            {participants.map((p) => (
              <li key={p.user_id} className="px-4 py-2 text-sm flex items-center justify-between">
                <span className="font-mono text-xs text-ink-300">{p.user_id.slice(0, 8)}…</span>
                <span className="text-xs text-ink-400">{p.role}</span>
              </li>
            ))}
            {participants.length === 0 && (
              <li className="px-4 py-3 text-sm text-ink-500">Brak uczestników</li>
            )}
          </ul>
          <p className="text-xs text-ink-500 mt-2">
            Link do dołączenia: <code className="text-ink-300">{location.origin}/lessons/{lesson.id}</code>
          </p>
        </section>

        {recordings.length > 0 && (
          <section>
            <h2 className="text-sm uppercase tracking-wider text-ink-400 mb-2">Nagrania</h2>
            <ul className="space-y-2">
              {recordings.map((r) => (
                <li key={r.id} className="bg-ink-800 border border-ink-700 rounded-lg px-4 py-2 text-sm flex items-center justify-between">
                  <span>
                    {fmt((r.ended_at ?? Date.now()))} — <span className="text-ink-400">{r.status}</span>
                  </span>
                  {r.status === "ended" && (
                    <a
                      href={`/api/recordings/${r.id}/download`}
                      className="text-lime-400 hover:text-lime-300 text-xs"
                    >
                      Pobierz
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {isHost && lesson.status !== "cancelled" && lesson.status !== "ended" && (
          <section>
            <button
              onClick={cancel}
              disabled={busy}
              className="w-full text-red-300 hover:text-red-200 border border-red-700 rounded-lg py-2 text-sm"
            >
              Anuluj lekcję
            </button>
          </section>
        )}
      </main>
    </div>
  );
}
